import type { Match } from '../../types';


/**
 * Function to draw text that automatically adjusts font size and wraps if needed
 */
  export function drawAutoSizedText(ctx, text, x, y, maxWidth, initialFontSize, minFontSize, fontFamily, maxLines = 2, fontWeight = 'bold') {
    let fontSize = initialFontSize;
    let lines = [text];
    
    // Store current text alignment to preserve it
    const originalAlign = ctx.textAlign;
    
    // First, try to fit the text by reducing font size
    while (fontSize >= minFontSize) {
      ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
      const textWidth = ctx.measureText(text).width;
      
      if (textWidth <= maxWidth) {
        // Text fits, draw it
        ctx.fillText(text, x, y);
        return y + fontSize * 1.2; // Return next Y position
      }
      
      fontSize -= 2; // Decrease font size by 2px each iteration
    }
    
    // If we reach minimum font size and text still doesn't fit, wrap it
    fontSize = minFontSize;
    ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
    
    // Split text into words and create lines that fit
    const words = text.split(' ');
    lines = [];
    let currentLine = '';
    
    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const testWidth = ctx.measureText(testLine).width;
      
      if (testWidth <= maxWidth || currentLine === '') {
        currentLine = testLine;
      } else {
        if (currentLine) {
          lines.push(currentLine);
        }
        currentLine = word;
        
        // Stop adding lines if we reach maxLines
        if (lines.length >= maxLines - 1) {
          // Add remaining words to the last line, truncated if necessary
          const remainingWords = words.slice(words.indexOf(word));
          let lastLine = remainingWords.join(' ');
          
          // Truncate with ellipsis if still too long
          while (ctx.measureText(lastLine + '...').width > maxWidth && lastLine.length > 0) {
            lastLine = lastLine.slice(0, -1);
          }
          if (lastLine !== remainingWords.join(' ')) {
            lastLine += '...';
          }
          
          lines.push(lastLine);
          break;
        }
      }
    }
    
    // Add the last line if it exists and we haven't reached max lines
    if (currentLine && lines.length < maxLines) {
      lines.push(currentLine);
    }
    
    // Draw all lines
    const lineHeight = fontSize * 1.2;
    lines.forEach((line, index) => {
      ctx.fillText(line, x, y + (index * lineHeight));
    });
    
    return y + (lines.length * lineHeight);
  }

/**
 * Draw a rounded rectangle with fallback for older browsers
 */
export function drawRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) {
  if (ctx.roundRect) {
    ctx.beginPath();
    ctx.roundRect(x, y, width, height, radius);
    ctx.fill();
  } else {
    // Fallback for older browsers
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.arcTo(x + width, y, x + width, y + height, radius);
    ctx.arcTo(x + width, y + height, x, y + height, radius);
    ctx.arcTo(x, y + height, x, y, radius);
    ctx.arcTo(x, y, x + width, y, radius);
    ctx.closePath();
    ctx.fill();
  }
}

/**
 * Draw a team logo with proper scaling and centering
 */
export function drawLogoScaled(
  ctx: CanvasRenderingContext2D,
  logo: HTMLImageElement | null,
  x: number,
  y: number,
  width: number,
  height: number,
  fallbackText?: string,
  fontFamily?: string
) {
  if (logo) {
    const scale = Math.min(width / logo.width, height / logo.height);
    const scaledWidth = logo.width * scale;
    const scaledHeight = logo.height * scale;

    const xOffset = (width - scaledWidth) / 2;
    const yOffset = (height - scaledHeight) / 2;

    ctx.drawImage(
      logo,
      x + xOffset,
      y + yOffset,
      scaledWidth,
      scaledHeight
    );
  } else if (fallbackText) {
    // Draw fallback placeholder
    ctx.strokeStyle = 'white';
    ctx.strokeRect(x, y, width, height);
    ctx.font = `bold 24px ${fontFamily || 'Arial'}`;
    ctx.textAlign = 'center';
    ctx.fillStyle = 'white';
    ctx.fillText(fallbackText, x + width / 2, y + height / 2);
  }
}

/**
 * Create a gradient overlay
 */
export function drawGradientOverlay(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  startOpacity: number = 0.6,
  endOpacity: number = 0
) {
  const gradient = ctx.createLinearGradient(0, 0, 0, height);
  gradient.addColorStop(0, `rgba(0, 0, 0, ${startOpacity})`);
  gradient.addColorStop(1, `rgba(0, 0, 0, ${endOpacity})`);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);
}

/**
 * Extract dominant color from an image using Canvas API
 * @param image - The HTMLImageElement to extract color from
 * @param sampleSize - Size of the sampling grid (default: 10x10)
 * @param darkenFactor - Factor to darken the color (0-1, default: 0.7 for 30% darker)
 * @returns RGB color object with r, g, b values (0-255)
 */
export function extractDominantColor(
  image: HTMLImageElement,
  sampleSize: number = 10,
  darkenFactor: number = 0.7
): { r: number; g: number; b: number } {
  try {
    // Create temporary canvas for color sampling
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('Could not get canvas context');
    }

    // Use small canvas for performance
    canvas.width = sampleSize;
    canvas.height = sampleSize;

    // Draw scaled-down image
    ctx.drawImage(image, 0, 0, sampleSize, sampleSize);

    // Get pixel data
    const imageData = ctx.getImageData(0, 0, sampleSize, sampleSize);
    const data = imageData.data;

    // Calculate average color
    let r = 0, g = 0, b = 0;
    const pixelCount = sampleSize * sampleSize;

    for (let i = 0; i < data.length; i += 4) {
      r += data[i];
      g += data[i + 1];
      b += data[i + 2];
      // data[i + 3] is alpha - we ignore it
    }

    // Average and darken
    return {
      r: Math.round((r / pixelCount) * darkenFactor),
      g: Math.round((g / pixelCount) * darkenFactor),
      b: Math.round((b / pixelCount) * darkenFactor)
    };
  } catch (error) {
    console.error('Error extracting dominant color:', error);
    // Fallback to black
    return { r: 0, g: 0, b: 0 };
  }
}

/**
 * Draw PANKLA.COM watermark
 */
export function drawPanklaWatermark(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number
) {
  ctx.fillStyle = 'white';
  ctx.textAlign = 'left';

  // Draw PANKLA in larger size
  ctx.font = 'bold 46px gagalin';
  ctx.fillText('PANKLA', x, y);

  // Draw .COM in smaller size with Archivo Black
  ctx.font = '900 25px "Archivo Black"';
  ctx.fillText('.COM', x + 150, y);
}

/**
 * Aggregate player statistics from match data
 */
export function getPlayerStats(report: Match) {
  // Get all players who either scored, assisted, or were Player of the match
  const allPlayers = [...new Set([
    // Get Player of the match players
    ...(report.player_awards || [])
      .filter(award => award.id === 'player-of-match')
      .map(award => award.name),
    // Get goal scorers from highlights
    ...(report.highlights || [])
      .filter(h => h.type === 'goal' && !h.is_opponent_goal)
      .flatMap(h => (h.actions || []).filter(a => a.action_type === 'goal-scorer').map(a => a.name)),
    // Get assist providers from highlights
    ...(report.highlights || [])
      .filter(h => h.type === 'goal' && !h.is_opponent_goal)
      .flatMap(h => (h.actions || []).filter(a => a.action_type === 'assist').map(a => a.name))
  ])];

  // Create the consolidated stats list
  const playerStats: Array<{
    player: string;
    goals: number;
    assists: number;
    isManOfMatch: boolean;
  }> = allPlayers.map(playerName => {
    // Check if player was Player of the match
    const isManOfMatch = (report.player_awards || [])
      .some(award => award.name === playerName && award.id === 'player-of-match');

    // Count goals and assists from highlights
    const goals = (report.highlights || [])
      .filter(h => h.type === 'goal' && !h.is_opponent_goal)
      .filter(h => (h.actions || []).some(a => a.name === playerName && a.action_type === 'goal-scorer'))
      .length;

    const assists = (report.highlights || [])
      .filter(h => h.type === 'goal' && !h.is_opponent_goal)
      .filter(h => (h.actions || []).some(a => a.name === playerName && a.action_type === 'assist'))
      .length;

    return {
      player: playerName,
      goals,
      assists,
      isManOfMatch
    };
  });

  // Sort alphabetically
  playerStats.sort((a, b) => a.player.localeCompare(b.player));

  return playerStats;
}

/**
 * Draw player stats section with single or two-column layout
 */
export function drawPlayerStats(
  ctx: CanvasRenderingContext2D,
  playerStats: Array<{
    player: string;
    goals: number;
    assists: number;
    isManOfMatch: boolean;
  }>,
  options: {
    x: number;
    y: number;
    width: number;
    fontFamily: string;
    sport: string; // 'football' or other
    columns: 1 | 2; // Single or two-column layout
    showTitle?: boolean;
    titleText?: string;
    showBackground?: boolean;
    backgroundColor?: string;
    borderRadius?: number;
    fontSize?: number;
    lineHeight?: number;
    maxPlayersPerColumn?: number;
  }
) {
  if (playerStats.length === 0) return;

  const {
    x,
    y,
    width,
    fontFamily,
    sport,
    columns,
    showTitle = true,
    titleText = 'GOAL SCORERS',
    showBackground = false,
    backgroundColor = 'rgba(0, 0, 0, 0.4)',
    borderRadius = 35,
    fontSize = 48,
    lineHeight = 80,
    maxPlayersPerColumn = 7
  } = options;

  const maxPlayers = columns === 1 ? playerStats.length : maxPlayersPerColumn * 2;
  const displayedPlayers = playerStats.slice(0, maxPlayers);

  // Calculate dimensions
  const titleHeight = showTitle ? 80 : 0;
  const playersPerColumn = columns === 1 ? displayedPlayers.length : Math.min(maxPlayersPerColumn, displayedPlayers.length);
  const totalHeight = (playersPerColumn * lineHeight) + titleHeight + 50;

  // Draw background if enabled
  if (showBackground) {
    ctx.fillStyle = backgroundColor;
    drawRoundedRect(ctx, x, y, width, totalHeight, borderRadius);
  }

  let currentY = y + 50;


  // Draw players
  ctx.fillStyle = 'white';
  ctx.font = `${fontSize >= 40 ? 'bold' : 'normal'} ${fontSize}px ${fontFamily}`;
  ctx.textAlign = 'left';

  const icon = sport === 'football' ? '⚽️' : '🏑';

  if (columns === 1) {
    // Single column layout
    displayedPlayers.forEach((playerStat, index) => {
      const goals = icon.repeat(playerStat.goals);
      const assists = '👏'.repeat(playerStat.assists);
      const manOfMatch = playerStat.isManOfMatch ? '👑' : '';

      ctx.fillText(
        `${playerStat.player} ${goals}${assists}${manOfMatch}`,
        x + 25,
        currentY + 25 + (index * lineHeight)
      );
    });
  } else {
    // Two column layout
    const columnWidth = width / 2;
    const column1X = x;
    const column2X = x + columnWidth;

    displayedPlayers.forEach((playerStat, index) => {
      const goals = icon.repeat(playerStat.goals);
      const assists = '👏'.repeat(playerStat.assists);
      const manOfMatch = playerStat.isManOfMatch ? '👑 ' : '';

      // Determine which column and row
      const isFirstColumn = index < maxPlayersPerColumn;
      const columnX = isFirstColumn ? column1X : column2X;
      const rowIndex = isFirstColumn ? index : index - maxPlayersPerColumn;
      const yPos = currentY + (rowIndex * lineHeight);

      ctx.fillText(
        `${manOfMatch}${playerStat.player} ${goals}${assists}`,
        columnX,
        yPos
      );
    });
  }

  return totalHeight;
}
